import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Link from '../components/Link';
import PageTitle from '../components/PageTitle';
import styles from './styles/Pedidos.module.css';
import { FaFileExcel } from 'react-icons/fa';

const formatarData = (data) => {
  if (!data) return '-';
  return new Date(data).toLocaleDateString('pt-BR');
};

const STATUS_PEDIDO_OPTIONS = ['ABERTO', 'EM SEPARAÇÃO', 'FINALIZADO', 'CANCELADO'];
const STATUS_PROGRESSO_OPTIONS = ['ABERTO', 'EM SEPARAÇÃO', 'FINALIZADO'];
const ORDEM_BANHO_METAL = [
  'BANHO DE OURO',
  'BANHO DE RÓDIO BRANCO',
  'BANHO DE RÓDIO NEGRO',
  'BANHO DE PRATA LEVE',
  'PRATA PURA',
  'AÇO PURO',
  'OUTROS',
];

function PedidoDetalhe() {
  const { id } = useParams();
  const token = localStorage.getItem('token');
  const permissoes = JSON.parse(localStorage.getItem('permissoes') || '{}');
  const somenteVisualizacao = permissoes?.pedidos?.somente_visualizacao === true || permissoes?.pedidos?.somente_visualizacao === 'true';

  const [pedido, setPedido] = useState(null);
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [imagemModal, setImagemModal] = useState(null);
  const [statusPendente, setStatusPendente] = useState('');
  const [separacaoPendente, setSeparacaoPendente] = useState(null);
  const [filtroSeparacao, setFiltroSeparacao] = useState('todos');
  const avisoTimeoutRef = useRef(null);

  const buscarPedido = useCallback(async () => {
    setCarregando(true);
    setErro('');

    try {
      const { data } = await axios.get(`https://provin.ind.br/api/pedidos/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPedido(data.pedido);
      setItens(data.itens);
    } catch (error) {
      console.error('Erro ao buscar pedido:', error);
      setErro('Erro ao buscar pedido.');
    } finally {
      setCarregando(false);
    }
  }, [id, token]);

  useEffect(() => {
    buscarPedido();
  }, [buscarPedido]);

  useEffect(() => {
    if (!imagemModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setImagemModal(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [imagemModal]);

  useEffect(() => () => {
    if (avisoTimeoutRef.current) {
      clearTimeout(avisoTimeoutRef.current);
    }
  }, []);

  const mostrarAviso = (mensagem) => {
    if (avisoTimeoutRef.current) {
      clearTimeout(avisoTimeoutRef.current);
    }

    setAviso(mensagem);
    avisoTimeoutRef.current = setTimeout(() => {
      setAviso('');
    }, 3000);
  };

  const resumoSeparacao = useMemo(() => {
    const total = itens.length;
    const separados = itens.filter((item) => item.separado).length;
    const percentual = total > 0 ? Math.round((separados / total) * 100) : 0;

    return { total, separados, percentual };
  }, [itens]);

  const gruposItens = useMemo(() => {
    const itensFiltrados = itens.filter((item) => {
      if (filtroSeparacao === 'separados') return Boolean(item.separado);
      if (filtroSeparacao === 'nao_separados') return !item.separado;
      return true;
    });

    const grupos = itensFiltrados.reduce((acc, item) => {
      const grupo = item.descr_grupo || 'Sem grupo';
      const banhoMetal = item.banho_metal || 'OUTROS';

      if (!acc[grupo]) {
        acc[grupo] = {};
      }

      if (!acc[grupo][banhoMetal]) {
        acc[grupo][banhoMetal] = [];
      }

      acc[grupo][banhoMetal].push(item);
      return acc;
    }, {});

    return Object.entries(grupos)
      .sort(([grupoA], [grupoB]) => grupoA.localeCompare(grupoB, 'pt-BR'))
      .map(([grupo, banhos]) => {
        const banhosOrdenados = Object.entries(banhos)
          .sort(([banhoA], [banhoB]) => {
            const indiceA = ORDEM_BANHO_METAL.indexOf(banhoA);
            const indiceB = ORDEM_BANHO_METAL.indexOf(banhoB);
            const ordemA = indiceA === -1 ? ORDEM_BANHO_METAL.length : indiceA;
            const ordemB = indiceB === -1 ? ORDEM_BANHO_METAL.length : indiceB;

            return ordemA - ordemB || banhoA.localeCompare(banhoB, 'pt-BR');
          })
          .map(([banhoMetal, itensBanho]) => ({
            banhoMetal,
            itens: [...itensBanho].sort((a, b) => String(a.cod_item).localeCompare(String(b.cod_item), 'pt-BR', { numeric: true })),
          }));

        return {
          grupo,
          totalItens: banhosOrdenados.reduce((total, banho) => total + banho.itens.length, 0),
          banhos: banhosOrdenados,
        };
      });
  }, [filtroSeparacao, itens]);

  const statusProgresso = useMemo(() => {
    const statusAtual = pedido?.status || 'ABERTO';
    const cancelado = statusAtual === 'CANCELADO';
    const indiceAtual = cancelado ? -1 : STATUS_PROGRESSO_OPTIONS.indexOf(statusAtual);
    const indiceSeguro = indiceAtual >= 0 ? indiceAtual : 0;
    const percentual = cancelado
      ? 100
      : (indiceSeguro / (STATUS_PROGRESSO_OPTIONS.length - 1)) * 100;

    return {
      cancelado,
      indiceAtual: indiceSeguro,
      percentual,
      statusAtual,
    };
  }, [pedido?.status]);

  const atualizarPedido = async (payload, mensagemSucesso = '') => {
    if (!pedido) return;

    const pedidoAnterior = pedido;
    setPedido((prev) => ({ ...prev, ...payload }));

    try {
      const { data } = await axios.put(
        `https://provin.ind.br/api/pedidos/${id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPedido((prev) => ({ ...prev, ...data }));
      if (mensagemSucesso) {
        mostrarAviso(mensagemSucesso);
      }
    } catch (error) {
      console.error('Erro ao atualizar pedido:', error);
      setPedido(pedidoAnterior);
      setErro('Erro ao atualizar pedido.');
    }
  };

  const solicitarAlteracaoStatus = (status) => {
    if (somenteVisualizacao || status === pedido?.status) return;

    setStatusPendente(status);
  };

  const confirmarAlteracaoStatus = async () => {
    if (!statusPendente) return;

    const proximoStatus = statusPendente;
    setStatusPendente('');
    await atualizarPedido({ status: proximoStatus });
  };

  const atualizarSeparado = async (itemId, separado) => {
    if (somenteVisualizacao) return;

    const itensAnteriores = itens;

    setItens((prev) => prev.map((item) => (
      item.id === itemId ? { ...item, separado } : item
    )));

    try {
      await axios.put(
        `https://provin.ind.br/api/pedidos/itens/${itemId}`,
        { separado },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error('Erro ao atualizar separação:', error);
      setItens(itensAnteriores);
      setErro('Erro ao atualizar separação do item.');
    }
  };

  const atualizarItem = async (itemId, payload) => {
    const itensAnteriores = itens;

    setItens((prev) => prev.map((item) => (
      item.id === itemId ? { ...item, ...payload } : item
    )));

    try {
      const { data } = await axios.put(
        `https://provin.ind.br/api/pedidos/itens/${itemId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setItens((prev) => prev.map((item) => (
        item.id === itemId ? { ...item, ...data } : item
      )));
      if (Object.prototype.hasOwnProperty.call(payload, 'anotacao')) {
        mostrarAviso('Anotação do item atualizada.');
      }
    } catch (error) {
      console.error('Erro ao atualizar item:', error);
      setItens(itensAnteriores);
      setErro('Erro ao atualizar item.');
    }
  };

  const alterarSeparacaoTodos = async (separado) => {
    if (somenteVisualizacao) return;

    const itensAnteriores = itens;

    setItens((prev) => prev.map((item) => ({ ...item, separado })));

    try {
      await axios.put(
        `https://provin.ind.br/api/pedidos/${id}/itens/separado`,
        { separado },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error('Erro ao atualizar separação em lote:', error);
      setItens(itensAnteriores);
      setErro('Erro ao atualizar separação dos itens.');
    }
  };

  const solicitarAlteracaoSeparacaoTodos = (separado) => {
    if (somenteVisualizacao) return;

    setSeparacaoPendente(separado);
  };

  const confirmarAlteracaoSeparacaoTodos = async () => {
    if (separacaoPendente === null) return;

    const proximoSeparado = separacaoPendente;
    setSeparacaoPendente(null);
    await alterarSeparacaoTodos(proximoSeparado);
  };

  const baixarExcelItens = async () => {
    if (!pedido) return;

    try {
      const response = await axios.get(`https://provin.ind.br/api/pedidos/${id}/itens-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `pedido_${id}_itens.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar Excel do pedido:', error);
      setErro('Erro ao baixar Excel do pedido.');
    }
  };

  return (
    <div className={styles.body}>
      <Header />

      <main className={styles.pageShell}>
        <Link redirectTo="/pedidos" />
        <PageTitle src="/images/common/anotacao.ico" title={`Pedido #${id}`} />

        {erro && <p className={styles.mensagemErro}>{erro}</p>}
        {aviso && <div className={styles.mensagemAviso} role="status">{aviso}</div>}

        <section className={styles.detalheHeader}>
          {carregando && !pedido ? (
            <p>Carregando pedido...</p>
          ) : pedido ? (
            <>
              <div>
                <span className={styles.labelResumo}>Usuário</span>
                <strong>{pedido.usuario}</strong>
              </div>
              <div>
                <span className={styles.labelResumo}>Data</span>
                <strong>{formatarData(pedido.dt_criacao)}</strong>
              </div>
              {/* <div>
                <span className={styles.labelResumo}>Tipo</span>
                <strong>{pedido.tipo_pedido || '-'}</strong>
              </div> */}
              {/* <div>
                <span className={styles.labelResumo}>Status</span>
                <strong>{pedido.status || '-'}</strong>
              </div> */}
              <div>
                <span className={styles.labelResumo}>Estoque alvo</span>
                <strong>{pedido.estoque_alvo ? `Empresa ${pedido.estoque_alvo}` : '-'}</strong>
              </div>
              <div className={styles.resumoSeparacao}>
                <span className={styles.labelResumo}>Separação</span>
                <strong>{resumoSeparacao.separados}/{resumoSeparacao.total} itens</strong>
                <div>
                  <i style={{ width: `${resumoSeparacao.percentual}%` }} />
                </div>
              </div>
            </>
          ) : (
            <p>Pedido não encontrado.</p>
          )}
        </section>

        {pedido && !somenteVisualizacao && (
          <section className={styles.statusPedidoCard}>
            <div>
              <span className={styles.labelResumo}>Status do pedido</span>
              <strong>{pedido.status || '-'}</strong>
            </div>

            <div className={styles.statusRadioGroup}>
              {STATUS_PEDIDO_OPTIONS.map((status) => (
                <label key={status} className={pedido.status === status ? styles.statusRadioAtivo : ''}>
                  <input
                    type="radio"
                    name="statusPedido"
                    value={status}
                    checked={pedido.status === status}
                    disabled={somenteVisualizacao}
                    onChange={() => solicitarAlteracaoStatus(status)}
                  />
                  <span>{status}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        {pedido && somenteVisualizacao && (
          <section className={`${styles.statusPedidoCard} ${styles.statusPedidoVisual}`}>
            <div className={styles.statusPedidoVisualHeader}>
              <div>
                <span className={styles.labelResumo}>Status do pedido</span>
                <strong>{statusProgresso.statusAtual}</strong>
              </div>
              {statusProgresso.cancelado && <span className={styles.statusCanceladoDestaque}>Cancelado</span>}
            </div>

            <div className={`${styles.statusTimeline} ${statusProgresso.cancelado ? styles.statusTimelineCancelado : ''}`}>
              <div className={styles.statusTimelineLinha}>
                <i style={{ width: `${statusProgresso.percentual}%` }} />
              </div>

              <div className={styles.statusTimelineEtapas}>
                {STATUS_PROGRESSO_OPTIONS.map((status, index) => {
                  const concluido = !statusProgresso.cancelado && index <= statusProgresso.indiceAtual;
                  const ativo = !statusProgresso.cancelado && index === statusProgresso.indiceAtual;

                  return (
                    <div
                      key={status}
                      className={`${styles.statusTimelineEtapa} ${concluido ? styles.statusTimelineConcluido : ''} ${ativo ? styles.statusTimelineAtivo : ''}`}
                    >
                      <span>{index + 1}</span>
                      <strong>{status}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {pedido && (
          <section className={styles.acoesPedido}>
            <div className={styles.acoesPedidoTopo}>
              <button type="button" className={styles.botaoExcel} onClick={baixarExcelItens}>
                <FaFileExcel aria-hidden="true" />
                Baixar Excel
              </button>
            </div>

            <label className={styles.observacoesPedido}>
              Observações
              <textarea
                rows="3"
                value={pedido.observacoes || ''}
                onChange={(e) => setPedido((prev) => ({ ...prev, observacoes: e.target.value }))}
                onBlur={(e) => {
                  if (!somenteVisualizacao) {
                    atualizarPedido({ observacoes: e.target.value }, 'Observações do pedido atualizadas.');
                  }
                }}
                maxLength={500}
                placeholder="Observações do pedido"
                readOnly={somenteVisualizacao}
              />
            </label>
          </section>
        )}

        <section className={styles.tabelaCard}>
          <div className={styles.atalhosSeparacao}>
            <button type="button" onClick={() => solicitarAlteracaoSeparacaoTodos(true)} disabled={somenteVisualizacao}>
              Marcar tudo
            </button>
            <button type="button" onClick={() => solicitarAlteracaoSeparacaoTodos(false)} disabled={somenteVisualizacao}>
              Limpar
            </button>
            <label className={styles.filtroSeparacaoItens}>
              Separação
              <select value={filtroSeparacao} onChange={(e) => setFiltroSeparacao(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="separados">Já separados</option>
                <option value="nao_separados">Ainda não separados</option>
              </select>
            </label>
          </div>

          <div className={styles.tabelaScroll}>
            <table className={styles.tabela}>
              <thead>
                <tr>
                  <th>Separado</th>
                  <th>Imagem</th>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Qtd.</th>
                  <th>Estoque</th>
                  <th>Anotação</th>
                </tr>
              </thead>
              <tbody>
                {carregando ? (
                  <tr>
                    <td colSpan="7" className={styles.estadoTabela}>Carregando itens...</td>
                  </tr>
                ) : itens.length === 0 ? (
                  <tr>
                    <td colSpan="7" className={styles.estadoTabela}>Nenhum item encontrado.</td>
                  </tr>
                ) : gruposItens.length === 0 ? (
                  <tr>
                    <td colSpan="7" className={styles.estadoTabela}>Nenhum item encontrado para este filtro.</td>
                  </tr>
                ) : (
                  gruposItens.map(({ grupo, totalItens, banhos }) => (
                    <React.Fragment key={grupo}>
                      <tr className={styles.linhaGrupoProduto}>
                        <td colSpan="7">
                          <span>{grupo}</span>
                          <strong>{totalItens} {totalItens === 1 ? 'item' : 'itens'}</strong>
                        </td>
                      </tr>

                      {banhos.map(({ banhoMetal, itens: itensBanho }) => (
                        <React.Fragment key={`${grupo}-${banhoMetal}`}>
                          <tr className={styles.linhaBanhoMetal}>
                            <td colSpan="7">
                              <span>{banhoMetal}</span>
                              <strong>{itensBanho.length} {itensBanho.length === 1 ? 'item' : 'itens'}</strong>
                            </td>
                          </tr>

                          {itensBanho.map((item) => (
                            <tr key={item.id} className={item.separado ? styles.itemSeparado : ''}>
                              <td>
                                <label className={styles.checkSeparado}>
                                  <input
                                    type="checkbox"
                                    checked={item.separado}
                                    disabled={somenteVisualizacao}
                                    onChange={(e) => atualizarSeparado(item.id, e.target.checked)}
                                  />
                                  <span />
                                </label>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className={styles.botaoImagemProduto}
                                  onClick={() => setImagemModal({
                                    codigo: item.cod_item,
                                    descricao: item.descr_longa,
                                    src: `https://provin.ind.br/api/imagem_produto/${String(item.cod_item).replace(/\//g, '+')}`,
                                  })}
                                >
                                  <img
                                    className={styles.thumbProduto}
                                    src={`https://provin.ind.br/api/imagem_produto/${String(item.cod_item).replace(/\//g, '+')}`}
                                    alt={item.cod_item}
                                  />
                                </button>
                              </td>
                              <td><strong>{item.cod_item}</strong></td>
                              <td>{item.descr_longa || '-'}</td>
                              <td>
                                <QuantidadeItem
                                  item={item}
                                  onAtualizar={atualizarItem}
                                />
                              </td>
                              <td>
                                {Number(item.estoque) > 0 ? (
                                  <span className={styles.estoquePedido}>{item.estoque}</span>
                                ) : (
                                  <span className={styles.semEstoque}>Sem estoque</span>
                                )}
                              </td>
                              <td>
                                <AnotacaoItem
                                  item={item}
                                  onAtualizar={atualizarItem}
                                  somenteVisualizacao={somenteVisualizacao}
                                />
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {imagemModal && (
          <div
            className={styles.modalImagemOverlay}
            onClick={() => setImagemModal(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`Imagem do produto ${imagemModal.codigo}`}
          >
            <div className={styles.modalImagemConteudo} onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                className={styles.fecharModalImagem}
                onClick={() => setImagemModal(null)}
                aria-label="Fechar imagem"
              >
                ×
              </button>
              <img src={imagemModal.src} alt={imagemModal.descricao || imagemModal.codigo} />
              <div>
                <strong>{imagemModal.codigo}</strong>
                {imagemModal.descricao && <span>{imagemModal.descricao}</span>}
              </div>
            </div>
          </div>
        )}

        {statusPendente && (
          <div className={styles.modalStatusOverlay} role="dialog" aria-modal="true" aria-label="Confirmar alteração de status">
            <div className={styles.modalStatus}>
              <span>Alterar status</span>
              <h3>{statusPendente}</h3>
              <p>Confirme para atualizar o status deste pedido.</p>

              <div>
                <button type="button" className={styles.botaoStatusCancelar} onClick={() => setStatusPendente('')}>
                  Cancelar
                </button>
                <button type="button" className={styles.botaoStatusConfirmar} onClick={confirmarAlteracaoStatus}>
                  Confirmar alteração
                </button>
              </div>
            </div>
          </div>
        )}

        {separacaoPendente !== null && (
          <div className={styles.modalStatusOverlay} role="dialog" aria-modal="true" aria-label="Confirmar alteração de separação">
            <div className={styles.modalStatus}>
              <span>Alterar separação</span>
              <h3>{separacaoPendente ? 'Marcar tudo' : 'Limpar marcações'}</h3>
              <p>
                {separacaoPendente
                  ? 'Confirme para marcar todos os itens deste pedido como separados.'
                  : 'Confirme para remover a marcação de separado de todos os itens deste pedido.'}
              </p>

              <div>
                <button type="button" className={styles.botaoStatusCancelar} onClick={() => setSeparacaoPendente(null)}>
                  Cancelar
                </button>
                <button type="button" className={styles.botaoStatusConfirmar} onClick={confirmarAlteracaoSeparacaoTodos}>
                  Confirmar alteração
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function QuantidadeItem({ item, onAtualizar }) {
  const [valor, setValor] = useState(item.qtd);

  useEffect(() => {
    setValor(item.qtd);
  }, [item.qtd]);

  return (
    <input
      className={styles.inputQuantidade}
      type="number"
      min="1"
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={() => onAtualizar(item.id, { qtd: Number(valor) || 1 })}
      readOnly
    />
  );
}

function AnotacaoItem({ item, onAtualizar, somenteVisualizacao }) {
  const [valor, setValor] = useState(item.anotacao || '');

  useEffect(() => {
    setValor(item.anotacao || '');
  }, [item.anotacao]);

  return (
    <textarea
      className={styles.inputAnotacao}
      rows="2"
      maxLength={500}
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={() => {
        if (!somenteVisualizacao) {
          onAtualizar(item.id, { anotacao: valor });
        }
      }}
      placeholder="Anotação"
      readOnly={somenteVisualizacao}
    />
  );
}

export default PedidoDetalhe;
